/** Client-side limits aligned with ap.orchestrator normalizeChatAttachments */
export const CHAT_ATTACHMENT_MAX_FILES = 5
export const CHAT_ATTACHMENT_MAX_BYTES = 2200000 // binary; base64 grows ~4/3

const EXT_TO_MIME = {
	png: 'image/png',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	gif: 'image/gif',
	webp: 'image/webp',
	svg: 'image/svg+xml',
	bmp: 'image/bmp',
	txt: 'text/plain',
	csv: 'text/csv',
	md: 'text/markdown',
	html: 'text/html',
	htm: 'text/html',
	css: 'text/css',
	json: 'application/json',
}

function mimeFromFilename(name) {
	const n = String(name || '').toLowerCase()
	const dot = n.lastIndexOf('.')
	if (dot === -1) return ''
	const ext = n.slice(dot + 1)
	return EXT_TO_MIME[ext] || ''
}

/**
 * Prefer File.type; else MIME from readAsDataURL prefix; else extension map.
 * Many browsers leave `file.type` empty even for valid images — without this,
 * ap.orchestrator treats the file as application/octet-stream and skips vision.
 *
 * @param {File} file
 * @param {string} dataUrlResult
 * @returns {string}
 */
export function resolveAttachmentMimeType(file, dataUrlResult) {
	const fromFile = file && file.type && file.type.split(';')[0]
	if (fromFile) return fromFile
	const m = typeof dataUrlResult === 'string' && /^data:([^;,]+)/i.exec(dataUrlResult)
	if (m && m[1]) return m[1].trim().toLowerCase()
	const fromName = mimeFromFilename(file && file.name)
	if (fromName) return fromName
	return 'application/octet-stream'
}

/**
 * @param {File} file
 * @returns {Promise<{ filename: string, mimeType: string, data: string }>}
 */
export function readFileAsChatAttachment(file) {
	return new Promise((resolve, reject) => {
		if (!file || file.size > CHAT_ATTACHMENT_MAX_BYTES) {
			const maxMb = Math.round(CHAT_ATTACHMENT_MAX_BYTES / 1024 / 1024)
			const msg = file
				? `File too large (max ${maxMb}MB): ${file.name}`
				: 'Invalid file'
			reject(new Error(msg))
			return
		}
		const reader = new FileReader()
		reader.onload = () => {
			const { result } = reader
			if (typeof result !== 'string' || !result.includes(',')) {
				reject(new Error('Could not read file'))
				return
			}
			const data = result.split(',')[1] || ''
			const mimeType = resolveAttachmentMimeType(file, result)
			resolve({
				filename: file.name || 'attachment',
				mimeType,
				data,
			})
		}
		reader.onerror = () => reject(reader.error || new Error('Read failed'))
		reader.readAsDataURL(file)
	})
}

/**
 * @param {FileList|File[]} files
 * @returns {Promise<{ filename: string, mimeType: string, data: string }[]>}
 */
export async function readFilesAsChatAttachments(files) {
	const list = Array.from(files || [])
	const out = []
	for (let i = 0; i < list.length && out.length < CHAT_ATTACHMENT_MAX_FILES; i += 1) {
		// eslint-disable-next-line no-await-in-loop
		out.push(await readFileAsChatAttachment(list[i]))
	}
	return out
}
